import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  TableRow as MuiTableRow,
  styled,
} from "@mui/material";
import { Close, Add, Delete as DeleteIcon } from "@mui/icons-material";
import { useState, useEffect } from "react";
import {
  criarTipologiasNovas,
  ICriarTipologiasNovasRequest,
  INovaTipologiaRequest,
} from "services/modules/tarefaDetalhe";
import { useNavigation } from "contexts/notificationsContext";
import { getProjetistas } from "services/modules/projetistas";
import { IProjetista } from "shared/interfaces/projetista";

// ============================================
// STYLED COMPONENTS
// ============================================

const NumberCircle = styled(Box)(({ theme }) => ({
  width: 24,
  height: 24,
  borderRadius: "50%",
  backgroundColor: "#3C90AD",
  color: "white",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "0.75rem",
  fontWeight: 600,
}));

// ============================================
// INTERFACES
// ============================================

interface ITipologiaNova {
  codigo: string;
  descricao: string;
  idProjetista: number | null;
  idLinha: number | null;
}

interface ISelectOption {
  value: number;
  label: string;
}

interface ModalCriarTipologiasNovasProps {
  isOpen: boolean;
  onClose: () => void;
  idTarefaCaso: number;
  onTipologiasCriadas: () => void;
  // 24/08 (pedido do PO): em Caso NOVO nada grava até o SALVAR — quando informado,
  // a confirmação devolve os dados digitados em vez de chamar a API.
  onSelecionarSemSalvar?: (tipologias: {
    codigo: string;
    descricao: string;
    idLinha: number;
    idProjetista: number;
    nomeLinha: string;
    nomeProjetista: string;
  }[]) => void;
}

// ============================================
// COMPONENTE
// ============================================

export const ModalCriarTipologiasNovas = ({
  isOpen,
  onClose,
  idTarefaCaso,
  onTipologiasCriadas,
  onSelecionarSemSalvar,
}: ModalCriarTipologiasNovasProps) => {
  const { openNotification } = useNavigation();
  const [loading, setLoading] = useState(false);
  const [tipologias, setTipologias] = useState<ITipologiaNova[]>([
    { codigo: "", descricao: "", idProjetista: null, idLinha: null },
  ]);
  // Item 7.3: combo Projetista (todos de Gerência > Projetistas) e Linha
  // dependente do projetista selecionado (cada IProjetista já traz suas linhas).
  const [projetistas, setProjetistas] = useState<IProjetista[]>([]);
  const [loadingOptions, setLoadingOptions] = useState(false);

  // Linhas disponíveis para o projetista selecionado em uma linha da tabela
  const linhasDoProjetista = (idProjetista: number | null): ISelectOption[] => {
    if (!idProjetista) return [];
    const proj = projetistas.find((p) => p.idProjetista === idProjetista);
    return (proj?.linhas || []).map((l) => ({ value: l.idLinha, label: l.nome }));
  };

  // Carregar opções de Projetistas (com suas linhas)
  const carregarOpcoes = async () => {
    setLoadingOptions(true);
    try {
      // Todos os projetistas de Gerência > Projetistas (sem paginação efetiva)
      const projetistasData = await getProjetistas({ page: 1, pageSize: 9999 });
      setProjetistas(projetistasData || []);
    } catch (error: any) {
      console.error("[ModalCriarTipologiasNovas] Erro ao carregar opções:", error);
      console.error("[ModalCriarTipologiasNovas] Detalhes do erro:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        url: error.config?.url,
      });
      openNotification({
        isOpen: true,
        message: error.response?.data?.message || "Erro ao carregar projetistas e linhas. Verifique o console para mais detalhes.",
        type: "error",
      });
    } finally {
      setLoadingOptions(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      carregarOpcoes();
      // Resetar formulário
      setTipologias([{ codigo: "", descricao: "", idProjetista: null, idLinha: null }]);
    }
  }, [isOpen]);

  const handleAdicionarLinha = () => {
    setTipologias([
      ...tipologias,
      { codigo: "", descricao: "", idProjetista: null, idLinha: null },
    ]);
  };

  const handleRemoverLinha = (index: number) => {
    if (tipologias.length === 1) {
      openNotification({
        isOpen: true,
        message: "É necessário ter pelo menos uma tipologia",
        type: "warning",
      });
      return;
    }
    setTipologias(tipologias.filter((_, i) => i !== index));
  };

  const handleChangeTipologia = (
    index: number,
    field: keyof ITipologiaNova,
    value: string | number | null
  ) => {
    const novasTipologias = [...tipologias];
    novasTipologias[index] = {
      ...novasTipologias[index],
      [field]: value,
    };
    // Ao trocar o Projetista, limpar a Linha (linhas dependem do projetista).
    if (field === "idProjetista") {
      novasTipologias[index].idLinha = null;
    }
    setTipologias(novasTipologias);
  };

  const validarFormulario = (): boolean => {
    for (let i = 0; i < tipologias.length; i++) {
      const t = tipologias[i];
      if (!t.codigo?.trim()) {
        openNotification({
          isOpen: true,
          message: `Tipologia ${i + 1}: Código é obrigatório`,
          type: "warning",
        });
        return false;
      }
      if (!t.descricao?.trim()) {
        openNotification({
          isOpen: true,
          message: `Tipologia ${i + 1}: Descrição é obrigatória`,
          type: "warning",
        });
        return false;
      }
      if (!t.idProjetista) {
        openNotification({
          isOpen: true,
          message: `Tipologia ${i + 1}: Projetista é obrigatório`,
          type: "warning",
        });
        return false;
      }
      if (!t.idLinha) {
        openNotification({
          isOpen: true,
          message: `Tipologia ${i + 1}: Linha é obrigatória`,
          type: "warning",
        });
        return false;
      }
    }
    return true;
  };

  const handleSalvar = async () => {
    if (!validarFormulario()) return;

    // 24/08: Caso novo — devolve os dados digitados; a gravação ocorre no SALVAR do Caso.
    if (onSelecionarSemSalvar) {
      onSelecionarSemSalvar(
        tipologias.map((t) => {
          const proj = projetistas.find((p) => p.idProjetista === t.idProjetista);
          const linha = (proj?.linhas || []).find((l) => l.idLinha === t.idLinha);
          return {
            codigo: t.codigo.trim(),
            descricao: t.descricao.trim(),
            idLinha: t.idLinha!,
            idProjetista: t.idProjetista!,
            nomeLinha: linha?.nome || "-",
            nomeProjetista: proj?.nome || "-",
          };
        })
      );
      openNotification({
        isOpen: true,
        message: `${tipologias.length} tipologia(s) adicionada(s) — serão gravadas ao salvar o Caso.`,
        type: "info",
      });
      onClose();
      return;
    }

    setLoading(true);
    try {
      const request: ICriarTipologiasNovasRequest = {
        idTarefaCaso,
        tipologias: tipologias.map((t) => ({
          codigo: t.codigo.trim(),
          descricao: t.descricao.trim(),
          idLinha: t.idLinha!,
          // FASE 04 (item 5a): o projetista ja era coletado e descartado no envio.
          idProjetista: t.idProjetista,
        })) as INovaTipologiaRequest[],
      };

      await criarTipologiasNovas(idTarefaCaso, request);

      openNotification({
        isOpen: true,
        message: `${tipologias.length} tipologia(s) criada(s) com sucesso!`,
        type: "success",
      });

      onTipologiasCriadas();
      onClose();
    } catch (error: any) {
      openNotification({
        isOpen: true,
        message: error.message || "Erro ao criar tipologias",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { minHeight: 500 },
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h6">Cadastro de novas tipologias</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Cadastre todas as tipologias que farão parte do caso
            </Typography>
          </Box>
          <Box display="flex" gap={1} alignItems="center">
            <Button
              variant="contained"
              color="primary"
              size="small"
              startIcon={<Add />}
              onClick={handleAdicionarLinha}
              sx={{ textTransform: "none" }}
            >
              ADICIONAR
            </Button>
            <IconButton onClick={onClose} size="small">
              <Close />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent>
        {loadingOptions ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell width={50}>#</TableCell>
                  <TableCell>Código</TableCell>
                  <TableCell>Descrição</TableCell>
                  <TableCell>Projetista</TableCell>
                  <TableCell>Linha</TableCell>
                  <TableCell width={60}>Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tipologias.map((tipologia, index) => (
                  <MuiTableRow key={index}>
                    <TableCell>
                      <NumberCircle>{index + 1}</NumberCircle>
                    </TableCell>
                    <TableCell>
                      <TextField
                        fullWidth
                        size="small"
                        value={tipologia.codigo}
                        onChange={(e) =>
                          handleChangeTipologia(index, "codigo", e.target.value)
                        }
                        placeholder="Ex: GOL-JVC324"
                        required
                        error={!tipologia.codigo?.trim()}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        fullWidth
                        size="small"
                        value={tipologia.descricao}
                        onChange={(e) =>
                          handleChangeTipologia(index, "descricao", e.target.value)
                        }
                        placeholder="Ex: JANELA VENEZIANA COM ABERTURA"
                        required
                        error={!tipologia.descricao?.trim()}
                      />
                    </TableCell>
                    <TableCell>
                      <FormControl fullWidth size="small" required>
                        <Select
                          value={tipologia.idProjetista || ""}
                          onChange={(e) =>
                            handleChangeTipologia(
                              index,
                              "idProjetista",
                              e.target.value ? Number(e.target.value) : null
                            )
                          }
                          displayEmpty
                          error={!tipologia.idProjetista}
                        >
                          <MenuItem value="">
                            <em>Selecione...</em>
                          </MenuItem>
                          {projetistas.map((proj) => (
                            <MenuItem key={proj.idProjetista} value={proj.idProjetista}>
                              {proj.nome}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </TableCell>
                    <TableCell>
                      <FormControl fullWidth size="small" required disabled={!tipologia.idProjetista}>
                        <Select
                          value={tipologia.idLinha || ""}
                          onChange={(e) =>
                            handleChangeTipologia(
                              index,
                              "idLinha",
                              e.target.value ? Number(e.target.value) : null
                            )
                          }
                          displayEmpty
                          error={!tipologia.idLinha}
                        >
                          <MenuItem value="">
                            <em>{tipologia.idProjetista ? "Selecione..." : "Selecione um projetista"}</em>
                          </MenuItem>
                          {linhasDoProjetista(tipologia.idProjetista).map((linha) => (
                            <MenuItem key={linha.value} value={linha.value}>
                              {linha.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleRemoverLinha(index)}
                        disabled={tipologias.length === 1}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </MuiTableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          color="inherit"
          sx={{
            textTransform: "uppercase",
            fontWeight: 600,
            minWidth: 120,
          }}
        >
          CANCELAR
        </Button>
        <Button
          onClick={handleSalvar}
          variant="contained"
          color="primary"
          disabled={loading || loadingOptions}
          sx={{
            textTransform: "uppercase",
            fontWeight: 600,
            minWidth: 120,
          }}
        >
          {loading ? <CircularProgress size={20} /> : "SALVAR"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

